/**
 * WhatsApp Cloud API (Graph) — templates e envio.
 * Envio POST /messages: path usa o campo conta da API interna (não o wabaid).
 * GET message_templates: path usa wabaid.
 * Proxy opcional: REACT_APP_WHATSAPP_GRAPH_MESSAGES_URL (URL completa …/messages).
 */

const GRAPH_API_VERSION = "v24.0"

/**
 * Base https://graph…/v24.0/{id} sem sufixo /messages — para listar templates (wabaid).
 * @param {string} wabaid
 */
export function getWhatsappGraphEntityBaseUrl(wabaid) {
    const fullMessages = process.env.REACT_APP_WHATSAPP_GRAPH_MESSAGES_URL
    if (fullMessages && String(fullMessages).trim() !== "") {
        const trimmed = String(fullMessages)
            .trim()
            .replace(/\/?messages\/?$/i, "")
        return trimmed.replace(/\/$/, "")
    }
    const id =
        (wabaid != null && String(wabaid).trim() !== "" && String(wabaid).trim()) ||
        (process.env.REACT_APP_WHATSAPP_GRAPH_WABA_ID &&
            String(process.env.REACT_APP_WHATSAPP_GRAPH_WABA_ID).trim()) ||
        "327996897058260"
    return `https://graph.facebook.com/${GRAPH_API_VERSION}/${id}`
}

/**
 * URL de envio POST …/messages — o ID no path vem do campo conta.
 * @param {string} conta
 */
export function getWhatsappGraphMessagesUrl(conta) {
    const fullMessages = process.env.REACT_APP_WHATSAPP_GRAPH_MESSAGES_URL
    if (fullMessages && String(fullMessages).trim() !== "") {
        return String(fullMessages).trim()
    }
    const id =
        (conta != null && String(conta).trim() !== "" && String(conta).trim()) ||
        (process.env.REACT_APP_WHATSAPP_GRAPH_WABA_ID &&
            String(process.env.REACT_APP_WHATSAPP_GRAPH_WABA_ID).trim()) ||
        "327996897058260"
    return `https://graph.facebook.com/${GRAPH_API_VERSION}/${id}/messages`
}

export function getWhatsappGraphToken() {
    return process.env.REACT_APP_WHATSAPP_GRAPH_TOKEN || ""
}

/**
 * @param {string} line
 * @returns {string|null} apenas dígitos, com DDI 55 quando omitido (Brasil)
 */
export function normalizeWhatsappPhone(line) {
    let d = String(line || "")
        .trim()
        .replace(/\D/g, "")
    if (!d) return null
    if (d.length <= 11 && !d.startsWith("55")) {
        d = "55" + d
    }
    return d
}

export function maxPlaceholderIndexInText(text) {
    if (!text || typeof text !== "string") return 0
    const re = /\{\{(\d+)\}\}/g
    let m
    let max = 0
    while ((m = re.exec(text)) !== null) {
        const n = parseInt(m[1], 10)
        if (!Number.isNaN(n)) max = Math.max(max, n)
    }
    return max
}

/**
 * Conta parâmetros de texto do cabeçalho e do corpo (placeholders {{n}}).
 * @param {object} template — item retornado por message_templates
 * @returns {{ headerCount: number, bodyCount: number, headerIsMedia: boolean }}
 */
export function countTemplateTextParameters(template) {
    const components = template?.components || []
    let headerCount = 0
    let bodyCount = 0
    let headerIsMedia = false

    for (const c of components) {
        const typ = String(c.type || "").toUpperCase()
        if (typ === "HEADER") {
            const fmt = String(c.format || "TEXT").toUpperCase()
            if (fmt === "TEXT" && c.text) {
                headerCount = maxPlaceholderIndexInText(c.text)
            } else if (["IMAGE", "VIDEO", "DOCUMENT"].includes(fmt)) {
                headerIsMedia = true
            }
        }
        if (typ === "BODY" && c.text) {
            bodyCount = maxPlaceholderIndexInText(c.text)
        }
    }
    return { headerCount, bodyCount, headerIsMedia }
}

export function templateLanguageCode(t) {
    const l = t?.language
    if (l == null) return ""
    if (typeof l === "string") return l
    if (typeof l === "object" && l.code != null) return String(l.code)
    return String(l)
}

export function templateOptionKey(t) {
    return `${t.name}:::${templateLanguageCode(t)}`
}

/**
 * GET …/{wabaid}/message_templates (Bearer token Graph)
 * @returns {Promise<{ ok: true, templates: object[] } | { ok: false, status: number, data: object }>}
 */
export async function fetchWhatsappMessageTemplates(wabaid, bearerToken) {
    const token = String(bearerToken || "").trim()
    if (!token) {
        return { ok: false, status: 0, data: { error: "missing_token" } }
    }

    const base = getWhatsappGraphEntityBaseUrl(wabaid)
    const all = []
    let url = `${base}/message_templates?limit=100`

    try {
        while (url) {
            const res = await fetch(url, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })
            const text = await res.text()
            let json
            try {
                json = text ? JSON.parse(text) : {}
            } catch {
                json = { raw: text }
            }

            if (!res.ok) {
                return { ok: false, status: res.status, data: json, templates: [] }
            }

            const chunk = json.data || []
            all.push(...chunk)
            url = json.paging?.next || null
        }

        return { ok: true, templates: all }
    } catch (e) {
        return {
            ok: false,
            status: 0,
            data: { error: String(e.message || e) },
            templates: [],
        }
    }
}

/**
 * @param {string} to - E.164 sem +
 * @param {{ name: string, languageCode: string, headerTexts?: string[], bodyTexts?: string[] }} template
 */
export function buildTemplatePayload(to, template) {
    const headerTexts = template.headerTexts || []
    const bodyTexts = template.bodyTexts || []

    const components = []
    if (headerTexts.length > 0) {
        components.push({
            type: "header",
            parameters: headerTexts.map(text => ({
                type: "text",
                text: (text != null && String(text).trim() !== "" ? String(text).trim() : " ") || " ",
            })),
        })
    }
    if (bodyTexts.length > 0) {
        components.push({
            type: "body",
            parameters: bodyTexts.map(text => ({
                type: "text",
                text: (text != null && String(text).trim() !== "" ? String(text).trim() : " ") || " ",
            })),
        })
    }

    const payload = {
        messaging_product: "whatsapp",
        to,
        type: "template",
        template: {
            name: template.name,
            language: { code: template.languageCode },
        },
    }
    if (components.length > 0) {
        payload.template.components = components
    }
    return payload
}

/**
 * @param {string} [tokenOverride]
 * @param {string} [conta] — ID no path de /messages (campo conta da config oficial)
 */
export async function sendWhatsappTemplateMessage(to, template, tokenOverride, conta) {
    const url = getWhatsappGraphMessagesUrl(conta)
    const trimmed =
        tokenOverride != null && String(tokenOverride).trim() !== ""
            ? String(tokenOverride).trim()
            : ""
    const token = trimmed !== "" ? trimmed : getWhatsappGraphToken()
    const body = buildTemplatePayload(to, template)

    const res = await fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
    })

    let data
    const text = await res.text()
    try {
        data = text ? JSON.parse(text) : {}
    } catch {
        data = text
    }

    return {
        ok: res.ok,
        status: res.status,
        data,
    }
}

/**
 * URL para criar/listar templates na WABA (mesmo path; criação é POST).
 * Equivale a: String.format("https://graph.facebook.com/v24.0/%s/message_templates", wabaId)
 * @see https://developers.facebook.com/docs/graph-api/reference/whats-app-business-account/message_templates
 */
export function getWhatsappMessageTemplatesCreateUrl(whatsappBusinessAccountId) {
    const wabaId = String(whatsappBusinessAccountId || "").trim()
    // Alinha com: String.format("https://graph.facebook.com/v24.0/%s/message_templates", wabaId)
    return `https://graph.facebook.com/${GRAPH_API_VERSION}/${wabaId}/message_templates`
}

/**
 * @param {string} whatsappBusinessAccountId
 * @param {string} bearerToken — token Graph com whatsapp_business_management
 * @param {object} templatePayload — name, language, category, allow_category_change?, components
 */
export async function createWhatsappMessageTemplate(
    whatsappBusinessAccountId,
    bearerToken,
    templatePayload
) {
    const url = getWhatsappMessageTemplatesCreateUrl(whatsappBusinessAccountId)
    const token = String(bearerToken || "").trim()
    const res = await fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(templatePayload),
    })

    let data
    const text = await res.text()
    try {
        data = text ? JSON.parse(text) : {}
    } catch {
        data = text
    }

    return {
        ok: res.ok,
        status: res.status,
        data,
    }
}
